/**
 * WebSocket Context Provider
 *
 * Ensures only one WebSocket connection is created and shared across the app
 */

import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
} from "react";
import { useAuth0 } from "@auth0/auth0-react";
import {
  WebSocketConnectionManager,
  WebSocketEventMap,
} from "./connectionManager";

type ConnectionState = "connecting" | "connected" | "disconnected";

interface WebSocketContextType {
  connectionState: ConnectionState;
  on<K extends keyof WebSocketEventMap>(
    eventType: K,
    handler: (data: WebSocketEventMap[K]) => void
  ): () => void;
  on(eventType: string, handler: (data: any) => void): () => void;
  reconnect: () => void;
}

const WebSocketContext = createContext<WebSocketContextType | null>(null);

// Get WebSocket URL from environment variable (read dynamically for testing)
const getWS_URL = () => process.env.REACT_APP_WEBSOCKET_URL || "";

// Singleton connection manager - shared across all components
let globalManager: WebSocketConnectionManager | null = null;
let managerInitialized = false;
let initializedWithUrl = "";

// Reset function for development
export function resetWebSocketManager() {
  if (globalManager) {
    globalManager.disconnect();
    globalManager = null;
  }
  managerInitialized = false;
  initializedWithUrl = "";
}

export function WebSocketProvider({ children }: { children: React.ReactNode }) {
  const { getAccessTokenSilently } = useAuth0();
  const [connectionState, setConnectionState] =
    useState<ConnectionState>("disconnected");
  const [managerVersion, setManagerVersion] = useState(0);
  const stateUpdateIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const getAuthToken = useCallback(async (): Promise<string | null> => {
    try {
      const token = await getAccessTokenSilently();
      return token;
    } catch (error) {
      console.error("Failed to get access token for WebSocket:", error);
      return null;
    }
  }, [getAccessTokenSilently]);

  useEffect(() => {
    const WS_URL = getWS_URL();
    // Don't connect if URL is not configured
    if (!WS_URL) {
      console.warn(
        "WebSocket URL not configured (REACT_APP_WEBSOCKET_URL), skipping connection"
      );
      return;
    }

    // If manager was initialized with a different URL, reset it
    if (managerInitialized && initializedWithUrl !== WS_URL) {
      if (globalManager) {
        globalManager.disconnect();
        globalManager = null;
      }
      managerInitialized = false;
      initializedWithUrl = "";
    }

    // Initialize singleton connection manager only once
    if (!managerInitialized) {
      globalManager = new WebSocketConnectionManager(WS_URL, getAuthToken);
      managerInitialized = true;
      initializedWithUrl = WS_URL;
      // Increment version to trigger re-subscription in consumers
      setManagerVersion((v) => v + 1);
    } else if (globalManager) {
      // Update auth callback if manager already exists (handles re-authentication)
      globalManager.updateAuthTokenCallback(getAuthToken);
    }

    if (!globalManager) {
      return;
    }

    // Update connection state periodically
    const updateState = () => {
      if (globalManager) {
        setConnectionState(globalManager.getConnectionState());
      }
    };

    // Connect if not already connected
    if (globalManager.getConnectionState() === "disconnected") {
      globalManager.connect();
    }

    updateState();

    // Set up state update interval
    stateUpdateIntervalRef.current = setInterval(updateState, 1000);

    // Cleanup on unmount
    return () => {
      if (stateUpdateIntervalRef.current) {
        clearInterval(stateUpdateIntervalRef.current);
        stateUpdateIntervalRef.current = null;
      }
    };
  }, [getAuthToken]);

  const on = useCallback(
    (eventType: string, handler: (data: any) => void) => {
      // Retry if globalManager isn't ready yet
      if (!globalManager) {
        let delayedUnsubscribe: (() => void) | null = null;
        let cancelled = false;

        setTimeout(() => {
          if (!cancelled && globalManager) {
            delayedUnsubscribe = globalManager.on(eventType, handler);
          }
        }, 100);

        return () => {
          cancelled = true;
          if (delayedUnsubscribe) {
            delayedUnsubscribe();
          }
        };
      }
      return globalManager.on(eventType, handler);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- managerVersion intentionally triggers new callback reference for re-subscription
    [managerVersion]
  );

  const reconnect = useCallback(() => {
    if (globalManager) {
      globalManager.disconnect();
      globalManager.connect();
    }
  }, []);

  return (
    <WebSocketContext.Provider
      value={{
        connectionState,
        on,
        reconnect,
      }}
    >
      {children}
    </WebSocketContext.Provider>
  );
}

export function useWebSocket(): WebSocketContextType {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error("useWebSocket must be used within WebSocketProvider");
  }
  return context;
}
